import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import useAuthStore from "../../../store/authStore";
import {
  getChallengePhrases,
  getChallengePhrasesAnswer,
  getUserAdmin,
  getUserInfo,
  insertOrUpdateChallengePhrasesAnswer,
  resetAuthKey,
  updateNotificationPreferences,
  updatePassword,
} from "../services/userProfileService";
import { getMfaSetup, verifyMfa } from "../../auth/service/auth_service";
import UserProfileNav from "../components/UserProfileNav";
import ChangePasswordPanel from "../components/ChangePasswordPanel";
import SecurityQuestionsPanel from "../components/SecurityQuestionsPanel";
import EmailPreferencesPanel from "../components/EmailPreferencesPanel";
import MfaPanel from "../components/MfaPanel";
import "../styles/UserProfile.css";
import useUserContextStore from "../../../store/userContextStore";

const resolveActiveTab = (profileType) => {
  if (profileType === "ChangeSecretQuestions") return "security";
  if (profileType === "ChangeEmailPreferences") return "email";
  if (profileType === "ConfigureMFA") return "mfa";
  return "password";
};

const resolveTabFromSection = (section) => {
  const key = String(section || "").trim().toLowerCase();
  if (key === "password" || key === "change-password") return "password";
  if (key === "security" || key === "security-questions" || key === "change-secret-questions") return "security";
  if (key === "email" || key === "email-preferences") return "email";
  if (key === "mfa" || key === "configure-mfa") return "mfa";
  return "";
};

const first = (data) => (Array.isArray(data) ? data[0] : null);

const UserProfilePage = () => {
  const { section, contactId: contactIdParam } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const globalUserContext = useUserContextStore((state)=>state.context)
  const setGlobalUserContext = useUserContextStore((state)=>state.setUserContext)
  const [context, setContext] = useState(null);
  

  const authUserId = useAuthStore((s) => s.user);
  const authUserName = useAuthStore((s) => s.inputUserName);
  const authRoleId = useAuthStore((s) => s.role);

  const [profileType,setProfileType] = useState(context?.userProfileType || "ChangePassword");
  const [isForgotPasswordMode,setIsForgotPasswordMode] = useState(profileType === "ChangeForgotPassword");
  const cId = context?.contactId ? atob(context?.contactId) : null
  const [contactId,setContactId] = useState(Number(cId) || Number(atob(contactIdParam)) || 0);
  const cuId = context?.currentUserId ? atob(context?.currentUserId) : null
  const [userId,setUserId] = useState(Number(cuId) || Number(authUserId) || 0);
  const [userEmail,setUserEmail] = useState(String(
    context?.email
    || context?.userEmail
    || context?.userName
    || authUserName
    || ""
  ).trim());

  const [activeTab, setActiveTab] = useState(resolveActiveTab(profileType));
  const [loading, setLoading] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [currentPassword, setCurrentPassword] = useState(
    profileType === "ChangePassword" ? "" : (context?.tempPassword || "")
  );
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showPostPasswordPrompt, setShowPostPasswordPrompt] = useState(false);

  const [questions, setQuestions] = useState([]);
  const [securityCurrentPassword, setSecurityCurrentPassword] = useState("");
  const [securityCurrentPasswordVisible, setSecurityCurrentPasswordVisible] = useState(false);
  const [answersVisible, setAnswersVisible] = useState({ answer1: false, answer2: false, answer3: false });
  const [securityValues, setSecurityValues] = useState({
    question1: "-1",
    answer1: "",
    question2: "-1",
    answer2: "",
    question3: "-1",
    answer3: "",
  });

  const [emailPreferences, setEmailPreferences] = useState({
    isDocEmail: false,
    isManagerCommentary: false,
    isTaxDocumentsNotification: false,
  });

  const [mfaConfigured, setMfaConfigured] = useState(Boolean(context?.isAuthKeySetUP));
  const [qrCodeImage, setQrCodeImage] = useState(context?.mfaQrCodeImage || "");
  const [otpCode, setOtpCode] = useState("");
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const mfaQrFetchState = useRef({ userId: 0, attempted: false, inFlight: false });

  const logout = useAuthStore((state) => state.logout);

  useEffect(()=>{
    document.title = "User Profile";
  },[])

  useEffect(()=>{
    if(globalUserContext){
      setContext(globalUserContext)
    }
  },[globalUserContext])

  useEffect(()=>{
    if(context){
      setProfileType(context.userProfileType || "ChangePassword")
      setIsForgotPasswordMode(context.userProfileType === "ChangeForgotPassword")
      setContactId(Number(atob(context.contactId)) || Number(atob(contactIdParam)) || 0)
      setUserId(Number(atob(context.currentUserId)) || Number(authUserId) || 0)
      setUserEmail(String(
        context.email
        || context.userEmail
        || context.userName
        || authUserName
        || ""
      ).trim())
      setCurrentPassword(context.userProfileType === "ChangePassword" ? "" : (context.tempPassword || ""))
      setMfaConfigured(Boolean(context?.isAuthKeySetUP))
      setQrCodeImage(context.mfaQrCodeImage || "")
    }
  },[context]);


  useEffect(() => {
    if (activeTab !== "mfa") {
      mfaQrFetchState.current.attempted = false;
    }
  }, [activeTab]);

  useEffect(() => {
    let cancelled = false;

    const loadMfaQrCode = async () => {
      if (activeTab !== "mfa") return;
      if (mfaConfigured) return;
      if (qrCodeImage) return;
      if (!userId) return;
      if (mfaQrFetchState.current.inFlight) return;
      if (mfaQrFetchState.current.userId === userId && mfaQrFetchState.current.attempted) return;

      try {
        mfaQrFetchState.current.inFlight = true;

        // If MFA was already configured earlier, don't force a re-setup flow.
        const existingUsers = await getUserAdmin({ userType: "any", userId });
        const existingList = Array.isArray(existingUsers) ? existingUsers : [];
        const existingUser = existingList.find((item) => Number(item?.UserId ?? item?.userId) === Number(userId)) || existingList[0] || null;
        const existingSecret = String(existingUser?.AuthKey ?? existingUser?.authKey ?? "").trim();

        // If the secret exists and we haven't started setup in this session, show as configured (reset-only UI).
        if (existingSecret && !mfaQrFetchState.current.attempted) {
          if (!cancelled) {
            setMfaConfigured(true);
            setQrCodeImage("");
          }
          return;
        }

        // Otherwise we need a QR code for setup; mark that we've initiated setup for this user in this session.
        mfaQrFetchState.current.userId = userId;
        mfaQrFetchState.current.attempted = true;

        const response = await getMfaSetup({
          userId: Number(userId),
          authKey: String(authUserName || userEmail || "").trim(),
        });

        const base64 = response?.data?.base64QrImage ?? response?.data?.Base64QrImage ?? "";
        if (!cancelled) setQrCodeImage(base64 || "");
      } catch {
        if (!cancelled) setQrCodeImage("");
      } finally {
        mfaQrFetchState.current.inFlight = false;
      }
    };

    loadMfaQrCode();
    return () => {
      cancelled = true;
    };
  }, [activeTab, authUserName, userEmail, mfaConfigured, qrCodeImage, userId]);

  useEffect(() => {
    let cancelled = false;

    const refreshMfaConfiguredFromDb = async () => {
      if (activeTab !== "mfa") return;
      if (!userId) return;
      if (qrCodeImage) return;
      if (mfaQrFetchState.current.inFlight) return;

      try {
        const adminUsers = await getUserAdmin({ userType: "any", userId });
        if (cancelled) return;

        const adminList = Array.isArray(adminUsers) ? adminUsers : [];
        const match = adminList.find((item) => Number(item?.UserId ?? item?.userId) === Number(userId)) || adminList[0] || null;
        const dbAuthKey = String(match?.AuthKey ?? match?.authKey ?? "").trim();
        setMfaConfigured(Boolean(dbAuthKey));
      } catch {
        // keep existing state
      }
    };

    refreshMfaConfiguredFromDb();

    return () => {
      cancelled = true;
    };
  }, [activeTab, qrCodeImage, userId]);

  useEffect(() => {
    const querySection = new URLSearchParams(location.search).get("section");
    const routeTab = resolveTabFromSection(section) || resolveTabFromSection(querySection);
    if (routeTab && !(isForgotPasswordMode && routeTab !== "password")) {
      setActiveTab(routeTab);
    }
    if (isForgotPasswordMode && routeTab && routeTab !== "password") {
      setActiveTab("password");
    }
  }, [isForgotPasswordMode, section, location.search]);

  const clearMessages = () => {
    setError("");
    setSuccess("");
  };

  const setErrorMessage = (message) => {
    setSuccess("");
    setError(message || "Unexpected error.");
    window.scrollTo(0, 0);
  };

  const setSuccessMessage = (message) => {
    setError("");
    setSuccess(message || "Success.");
    window.scrollTo(0, 0);
  };

  const loadQuestions = async (isInitialFlow) => {
    debugger;
      if(!isInitialFlow){
        try {
          const [list, existing] = await Promise.all([
            getChallengePhrases(),
            userId ? getChallengePhrasesAnswer(userId) : Promise.resolve([]),
          ]);
          const mappedQuestions = Array.isArray(list) ? list : [];
          const mappedExisting = Array.isArray(existing) ? existing : [];

          setQuestions(mappedQuestions);

          setSecurityValues((prev) => ({
            ...prev,
            question1: String(mappedExisting[0]?.ChallengePhraseId ?? mappedExisting[0]?.challengePhraseId ?? prev.question1),
            answer1: mappedExisting[0]?.Answer ?? mappedExisting[0]?.answer ?? prev.answer1,
            question2: String(mappedExisting[1]?.ChallengePhraseId ?? mappedExisting[1]?.challengePhraseId ?? prev.question2),
            answer2: mappedExisting[1]?.Answer ?? mappedExisting[1]?.answer ?? prev.answer2,
            question3: String(mappedExisting[2]?.ChallengePhraseId ?? mappedExisting[2]?.challengePhraseId ?? prev.question3),
            answer3: mappedExisting[2]?.Answer ?? mappedExisting[2]?.answer ?? prev.answer3,
          }));
        } catch(e) {
          setErrorMessage("Failed to load security questions.1");
          console.log(e)
        }
      }
      else{
        try {
        const list = await getChallengePhrases();

        const mappedQuestions = Array.isArray(list) ? list : [];

        setQuestions(mappedQuestions);

        setSecurityValues((prev) => ({
          ...prev,
          // question1: String(mappedExisting[0]?.ChallengePhraseId ?? mappedExisting[0]?.challengePhraseId ?? prev.question1),
          // answer1: mappedExisting[0]?.Answer ?? mappedExisting[0]?.answer ?? prev.answer1,
          // question2: String(mappedExisting[1]?.ChallengePhraseId ?? mappedExisting[1]?.challengePhraseId ?? prev.question2),
          // answer2: mappedExisting[1]?.Answer ?? mappedExisting[1]?.answer ?? prev.answer2,
          // question3: String(mappedExisting[2]?.ChallengePhraseId ?? mappedExisting[2]?.challengePhraseId ?? prev.question3),
          // answer3: mappedExisting[2]?.Answer ?? mappedExisting[2]?.answer ?? prev.answer3,
        }));
      } catch {
        setErrorMessage("Failed to load security questions.2");
      }
      }
    };

  useEffect(() => {
    let cancelled = false;

    if (isForgotPasswordMode || activeTab !== "security") {
      return () => {
        cancelled = true;
      };
    }

    

    loadQuestions(false);
    return () => {
      cancelled = true;
    };
  }, [activeTab, isForgotPasswordMode, userId]);

  useEffect(() => {
    let cancelled = false;

    if (activeTab !== "email" || !contactId) {
      return () => {
        cancelled = true;
      };
    }

    const loadPreferences = async () => {
      try {
        const response = await getUserInfo(contactId);
        if (cancelled) return;
        const info = first(response);
        if (!info) return;

        setEmailPreferences({
          isDocEmail: Boolean(info.IsDocEmail ?? info.isDocEmail),
          isManagerCommentary: Boolean(info.IsManagerCommentary ?? info.isManagerCommentary),
          isTaxDocumentsNotification: Boolean(info.IsTaxDocumentsNotification ?? info.isTaxDocumentsNotification),
        });
      } catch {
        if (!cancelled) setErrorMessage("Failed to load email preferences.");
      }
    };

    loadPreferences();
    return () => {
      cancelled = true;
    };
  }, [activeTab, contactId]);

  const handleUpdatePassword = async () => {
    clearMessages();

    // if (!currentPassword && profileType !== "ChangeForgotPassword") {
    //   setErrorMessage("Please enter your current password");
    //   return;
    // }
    if (!newPassword) {
      setErrorMessage("Please enter your new password");
      return;
    }
    if (!confirmPassword) {
      setErrorMessage("Please enter retype password");
      return;
    }
    if (newPassword !== confirmPassword) {
      setErrorMessage("Please enter the same password");
      return;
    }

    setLoading(true);
    try {
      if (profileType === "ChangeForgotPassword") {
        const forgotUserName = String(context.userName || authUserName || "").trim();
        const uId = context.currentUserId ? atob(context.currentUserId): (context.userId?atob(context.userId):userId) 
        const forgotUserId = Number(uId) || 0;
        const forgotRoleId = Number(context.roleId ?? authRoleId ?? 0);

        if (!forgotUserName || !forgotUserId) {
          setErrorMessage("Unable to resolve forgot-password session. Please login again.");
          setLoading(false);
          return;
        }

        const result = await updatePassword({
          Email: forgotUserName,
          CurrentPassword: "",
          UpdatePassword: newPassword,
          ResetPassword: confirmPassword,
          PasswordTemporary: false,
          LoginAttempts: 0,
          LastPasswordReset: new Date().toISOString(),
          IsUser: [1,2].includes(Number(forgotRoleId))? 3 : 4,
          Message: "",
          IsCC: false,
          IsForgotPasswordFlow: true,
          TemplateType: 0,
          IsTempPasswordActive: true,
          ResetPasswordUniqueID: String(context.resetPasswordUniqueID || ""),
          IPAddress: String(context.ipAddress || ""),
          RequestedByUserId: forgotUserId,
        });

        const message = result?.Message ?? result?.message;
        if (message === "Successfully your password has been Reset") {
          const forgotFlowNextPage = String(context.postPasswordNextPageKey || "").trim().toUpperCase();
          const resolvedUserName = String(context.userName || authUserName || userEmail || "").trim();

          setCurrentPassword("");
          setNewPassword("");
          setConfirmPassword("");
          setShowCurrent(false);
          setShowNew(false);
          setShowConfirm(false);
          //setShowPostPasswordPrompt(false);
          setSuccessMessage(message);

          if (forgotFlowNextPage !== "CHALLENGEPHRASE") {
            setSecurityCurrentPassword("");
          }

          if (forgotFlowNextPage === "CHALLENGEPHRASE") {
            if(result?.token){
              setToken(result?.token);
            }
            // navigate("/disclaimer", {
            //   state: {
            //     userId: forgotUserId,
            //     userName: resolvedUserName,
            //     password: newPassword,
            //   },
            // });
            //setShowPostPasswordPrompt(true)
            loadQuestions(true); setActiveTab("security"); setShowPostPasswordPrompt(false)
            return;
          }

          window.location.href = "/";
          return;
        }

        if (message === "Current Password does not match") {
          setErrorMessage(message);
          setSecurityCurrentPassword("");
        } else {
          setErrorMessage("Some exception occured while reseting the password.");
          setSecurityCurrentPassword("");
        }
        setLoading(false);
        return;
      }
      const uId = context?.currentUserId ? atob(context?.currentUserId): (context?.userId ? atob(context?.userId) : (context?.userID ? atob(context?.userID):userId))
      const lookupUserId = Number(uId) || userId || 0;
      const lookupUserName = userEmail;

      const adminUsers = await getUserAdmin({
        userType: "any",
        userId: lookupUserId,
        userName: lookupUserId > 0 ? "" : lookupUserName,
      });

      const adminList = Array.isArray(adminUsers) ? adminUsers : [];
      let admin = null;

      if (lookupUserId > 0) {
        admin = adminList.find(
          (item) => Number(item?.UserId ?? item?.userId) === lookupUserId
        ) || null;
      }

      if (!admin && lookupUserName) {
        const normalizedLookup = lookupUserName.toLowerCase();
        admin = adminList.find((item) => {
          const userNameMatch = String(item?.UserName ?? item?.userName ?? "").toLowerCase() === normalizedLookup;
          const emailMatch = String(item?.EmailAddress ?? item?.emailAddress ?? "").toLowerCase() === normalizedLookup;
          return userNameMatch || emailMatch;
        }) || null;
      }

      if (!admin && adminList.length === 1) {
        admin = adminList[0];
      }

      if (!admin) {
        setErrorMessage("Unable to resolve logged-in user details for password update.");
        setLoading(false);
        return;
      }

      const roleId = Number(admin?.RoleId ?? admin?.roleId ?? 0);
      const derivedEmail = String(
        [1,2].includes(Number(roleId))
          ? (admin?.EmailAddress ?? admin?.emailAddress ?? userEmail)
          : (admin?.UserName ?? admin?.userName ?? userEmail)
      ).trim();
      const derivedRequestedByUserId = Number(admin?.UserId ?? admin?.userId) || userId;

      const result = await updatePassword({
        Email: derivedEmail,
        CurrentPassword: "",
        UpdatePassword: newPassword,
        ResetPassword: confirmPassword,
        PasswordTemporary: Boolean(admin?.PasswordTemporary ?? admin?.passwordTemporary ?? context?.passwordTemporary),
        LoginAttempts: Number(context?.loginAttempts) || 0,
        LastPasswordReset: new Date().toISOString(),
        IsUser: [1,2].includes(Number(roleId))? 3 : 4,
        Message: "",
        IsCC: Boolean(context?.isCC),
        IsForgotPasswordFlow: profileType === "ChangeForgotPassword",
        TemplateType: Number(context?.templateType) || 0,
        IsTempPasswordActive: Boolean(admin?.PasswordTemporary ?? admin?.passwordTemporary ?? context?.isTempPasswordActive),
        ResetPasswordUniqueID: String(context?.resetPasswordUniqueID || ""),
        IPAddress: String(context?.ipAddress || ""),
        RequestedByUserId: derivedRequestedByUserId,
      });

      const message = result?.Message ?? result?.message;
      if (message === "Successfully your password has been Reset") {
        const forgotFlowNextPage = String(context?.postPasswordNextPageKey || "").trim().toUpperCase();
        const resolvedUserName = String(context?.userName || authUserName || userEmail || "").trim();

        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
        setShowCurrent(false);
        setShowNew(false);
        setShowConfirm(false);
        setShowPostPasswordPrompt(false);
        setSuccessMessage(message);
        loadQuestions(true); 
        setActiveTab("security");
        if (forgotFlowNextPage !== "CHALLENGEPHRASE") {
            setSecurityCurrentPassword("");
          }
      } else if (message === "Current Password does not match") {
        setErrorMessage(message);
        setSecurityCurrentPassword("");
      } else {
        setErrorMessage("Some exception occured while reseting the password.");
        setSecurityCurrentPassword("");
      }
    } catch(e) {
      setErrorMessage("Some exception occured while reseting the password.");
      setSecurityCurrentPassword("");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSecurityQuestions = async () => {
    clearMessages();

    const q1 = securityValues.question1;
    const q2 = securityValues.question2;
    const q3 = securityValues.question3;
    const a1 = securityValues.answer1;
    const a2 = securityValues.answer2;
    const a3 = securityValues.answer3;

    // if (!securityCurrentPassword) {
    //   setErrorMessage("Please enter your current password");
    //   return;
    // }
    if (!q1 || q1 === "-1") {
      setErrorMessage("Security Question 1 is required");
      return;
    }
    if (!a1) {
      setErrorMessage("Security Question 1 Answer is required");
      return;
    }
    if (!q2 || q2 === "-1") {
      setErrorMessage("Security Question 2 is required");
      return;
    }
    if (!a2) {
      setErrorMessage("Security Question 2 Answer is required");
      return;
    }
    if (!q3 || q3 === "-1") {
      setErrorMessage("Security Question 3 is required");
      return;
    }
    if (!a3) {
      setErrorMessage("Security Question 3 Answer is required");
      return;
    }
    if (q1 === q2 || q1 === q3 || q2 === q3) {
      setErrorMessage("Security Question should not be same.");
      return;
    }

    if (!contactId) {
      setErrorMessage("Unable to resolve logged-in user details for security questions update.");
      return;
    }

    const payload = [
      { UserId: contactId, CurrentPassword: "", ChallengePhraseId: Number(q1), ChallengePhrase: a1, SortOrder: 1 },
      { UserId: contactId, CurrentPassword: "", ChallengePhraseId: Number(q2), ChallengePhrase: a2, SortOrder: 2 },
      { UserId: contactId, CurrentPassword: "", ChallengePhraseId: Number(q3), ChallengePhrase: a3, SortOrder: 3 },
    ];

    setLoading(true);
    try {
      const result = await insertOrUpdateChallengePhrasesAnswer(payload);
      if (Number(result) > 0) {
        setSecurityCurrentPassword("");
        setSecurityCurrentPasswordVisible(false);
        setAnswersVisible({ answer1: false, answer2: false, answer3: false });
        setSecurityValues({
          question1: "-1",
          answer1: "",
          question2: "-1",
          answer2: "",
          question3: "-1",
          answer3: "",
        });
        setSuccessMessage("Your Security Question are changed Successfully.");
        if(profileType === "ChangeForgotPassword"){
          loginRedirectAlert();
        }
        else{
          loadQuestions();
        }
      } else if (Number(result) === -3) {
        setErrorMessage("Current Password does not match.Please try again.");
      } else {
        setErrorMessage("Some exception occured while reseting the password.");
      }
    } catch {
      setErrorMessage("Some exception occured while reseting the password.");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateEmailPreferences = async () => {
    clearMessages();

    setLoading(true);
    try {
      const result = await updateNotificationPreferences({
        ContactId: contactId,
        IsNotificationSubscribed: emailPreferences.isDocEmail,
        IsManagerCommentary: emailPreferences.isManagerCommentary,
        IsTaxDocumentsNotification: emailPreferences.isTaxDocumentsNotification,
      });

      if (Number(result) !== 0) {
        setSuccessMessage("Email Preferences Updated Successfully.");
      } else {
        setErrorMessage("Some Exception Occured While Updating Email Preferences.");
      }
    } catch {
      setErrorMessage("Some Exception Occured While Updating Email Preferences.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyMfaCode = async () => {
    clearMessages();

    if (!/^\d{6}$/.test(String(otpCode || ""))) {
      setErrorMessage("Please enter a valid 6-digit OTP code.");
      return;
    }
    if (!userId) {
      setErrorMessage("Unable to resolve logged-in user details for MFA verification.");
      return;
    }

    setLoading(true);
    try {
      const response = await verifyMfa({
        userId: Number(userId),        
        authKey: String(authUserName),
        authToken: String(otpCode || "").trim(),
      });

      const status = response?.data?.status ?? response?.data?.Status ?? "";
      if (String(status).toUpperCase() === "SUCCESS") {
        setMfaConfigured(true);
        setQrCodeImage("");
        setOtpCode("");
        setSuccessMessage("Device has been authenticated");
      } else {
        setErrorMessage(response?.data?.message ?? response?.data?.Message ?? "Invalid Authentication Code");
      }
    } catch {
      setErrorMessage("Invalid Authentication Code");
    } finally {
      setLoading(false);
    }
  };

  const handleResetMfa = async () => {
    clearMessages();
    setLoading(true);

    try {
      const type = [1,2].includes(Number(authRoleId)) ? "Admin" : "Contact";
      const result = await resetAuthKey({
        UserId: userId,
        Type: type,
      });
      if (result) {
        setMfaConfigured(false);
        setShowResetConfirm(false);
        setSuccessMessage("MFA Key reset Successfully");
        setQrCodeImage("");
        setOtpCode("");
        mfaQrFetchState.current.attempted = false;
      } else {
        setErrorMessage("Some Exception Occured Please Contact Admin");
      }
    } catch {
      setErrorMessage("Some Exception Occured Please Contact Admin");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    const returnPage = String(context?.returnPage || "").trim();
    if (returnPage) {
      window.location.href = `/${returnPage.replace(/^\/+/, "")}`;
      return;
    }
    window.history.back();
  };

  const handlePostPasswordPromptNo = () => {
    setShowPostPasswordPrompt(false);
    loginRedirectAlert();
  };

  const loginRedirectAlert = () => {
    window.alert("Your password has been changed successfully, Security Questions has been set. You can now login with your new Password to continue!");
    logout();
    window.location.href = "/";
    return;
  }

  return (
    <div className="minHeightDiv1" id="DivUserProfile">
      <div className="container-lg container-fluid-md">
        <article className="white_box_profile user-profile-shell">
          <div className="row">
            {(error || success) && (
              <div className="col-sm-12 user-profile-message-wrap">
                {success && <h5 className="user-profile-success">{success}</h5>}
                {error && <h5 className="user-profile-error">{error}</h5>}
              </div>
            )}
          </div>

          <div className="row user-profile-main-row">
            <div className="col-sm-4">
              <UserProfileNav
                activeTab={activeTab}
                onTabChange={(tab) => {
                  clearMessages();
                  setActiveTab(isForgotPasswordMode ? tab == "password" ? "password"
                                                                        :tab == "security" ? "security"
                                                                                            :  tab 
                                                    :tab);
                }}
                tabs={isForgotPasswordMode ? [{ id: "password", label: "Change Password" }, { id: "security", label: "Create/Update security question" }] : undefined}
              />
            </div>
            <div className="col-sm-8 user-profile-content-area">
              {loading && <div className="user-profile-loading">Loading...</div>}

              {activeTab === "password" && (
                <ChangePasswordPanel
                  profileType={profileType}
                  currentPassword={currentPassword}
                  newPassword={newPassword}
                  confirmPassword={confirmPassword}
                  showCurrent={showCurrent}
                  showNew={showNew}
                  showConfirm={showConfirm}
                  onCurrentPasswordChange={setCurrentPassword}
                  onNewPasswordChange={(value) => {
                    setNewPassword(value)
                    if(isForgotPasswordMode){
                      setSecurityCurrentPassword(value)
                    }
                  }}
                  onConfirmPasswordChange={setConfirmPassword}
                  onToggleCurrent={() => setShowCurrent((v) => !v)}
                  onToggleNew={() => setShowNew((v) => !v)}
                  onToggleConfirm={() => setShowConfirm((v) => !v)}
                  onSubmit={handleUpdatePassword}
                  onCancel={handleCancel}
                  showPostSuccessPrompt={showPostPasswordPrompt}
                  onPostSuccessYes={() => {loadQuestions(true); setActiveTab("security"); setShowPostPasswordPrompt(false)}}
                  onPostSuccessNo={handlePostPasswordPromptNo}
                />
              )}

              {activeTab === "security" && (
                <SecurityQuestionsPanel
                  currentPassword={securityCurrentPassword}
                  onCurrentPasswordChange={setSecurityCurrentPassword}
                  currentPasswordVisible={securityCurrentPasswordVisible}
                  onToggleCurrentPassword={() => setSecurityCurrentPasswordVisible((v) => !v)}
                  questions={questions}
                  values={securityValues}
                  answerVisible={answersVisible}
                  onQuestionValueChange={(key, value) => setSecurityValues((prev) => ({ ...prev, [key]: value }))}
                  onAnswerValueChange={(key, value) => setSecurityValues((prev) => ({ ...prev, [key]: value }))}
                  onToggleAnswer={(key) => setAnswersVisible((prev) => ({ ...prev, [key]: !prev[key] }))}
                  onSubmit={handleUpdateSecurityQuestions}
                  onCancel={handleCancel}
                />
              )}

              {activeTab === "email" && (
                <EmailPreferencesPanel
                  values={emailPreferences}
                  onToggle={(key, checked) => setEmailPreferences((prev) => ({ ...prev, [key]: checked }))}
                  onUpdate={handleUpdateEmailPreferences}
                  onCancel={handleCancel}
                />
              )}

              {activeTab === "mfa" && (
                <MfaPanel
                  mfaConfigured={mfaConfigured}
                  qrCodeImage={qrCodeImage}
                  otpCode={otpCode}
                  onOtpCodeChange={setOtpCode}
                  onVerify={handleVerifyMfaCode}
                  onShowReset={() => setShowResetConfirm((v) => !v)}
                  onReset={handleResetMfa}
                  showResetConfirm={showResetConfirm}
                />
              )}
            </div>
          </div>
        </article>
      </div>
    </div>
  );
};

export default UserProfilePage;
